/**
 * Centralized handler for `403 tier_limit` responses from core. Pushes a
 * helpful toast and either nudges toward /tiers (when SystemSettings.
 * tiersVisible is on) or shows a softer message (when it's off, since
 * there's no upgrade path to point the user at).
 */
import { useToast } from '../components/ToastHost';
import { usePublicConfig } from './public-config';

interface TierLimitBody {
  error: 'tier_limit';
  feature: 'groups' | 'sets' | 'links' | 'contacts' | 'voice';
  limit: number;
  current: number;
  tier: string | null;
}

const FEATURE_NOUN: Record<TierLimitBody['feature'], string> = {
  groups: 'groups',
  sets: 'sets in this group',
  links: 'links in this set',
  contacts: 'contacts',
  voice: 'voice search',
};

function isTierLimit(body: unknown): body is TierLimitBody {
  return (
    !!body &&
    typeof body === 'object' &&
    (body as { error?: string }).error === 'tier_limit' &&
    typeof (body as { feature?: string }).feature === 'string'
  );
}

/**
 * Returns true if `err` was a tier_limit and was handled (toast pushed).
 * Callers in mutation onError handlers should `if (handleTierError(err)) return;`
 * before falling back to their generic error toast.
 */
export function handleTierError(err: unknown): boolean {
  const body = (err as { body?: unknown })?.body;
  if (!isTierLimit(body)) return false;

  // Mark so pushError() in onError handlers knows this is already toasted.
  if (err && typeof err === 'object') (err as { handled?: boolean }).handled = true;

  const tiersVisible = usePublicConfig.getState()?.config?.tiersVisible ?? false;
  const noun = FEATURE_NOUN[body.feature];

  if (body.feature === 'voice') {
    useToast.getState().push({
      severity: tiersVisible ? 'warning' : 'info',
      message: tiersVisible
        ? `Voice search isn’t included on the ${body.tier ?? 'current'} tier. View tiers in your account menu.`
        : 'Voice search isn’t available on your account right now.',
    });
    return true;
  }

  useToast.getState().push({
    severity: tiersVisible ? 'warning' : 'info',
    message: tiersVisible
      ? `You’ve reached your limit of ${body.limit} ${noun} on the ${body.tier ?? 'current'} tier. Open your account menu to view tiers.`
      : `Your account allows up to ${body.limit} ${noun}.`,
  });
  return true;
}
